import { InputAdornment, TextField as MuiTextField, TextFieldProps, Tooltip } from '@mui/material';
import NumberField from 'components/number-field';
import { Info } from 'lucide-react';
import React from 'react';
import slugify from 'utils/slugify';

type TextFieldContentProps = TextFieldProps & {
  label: string;
  hiddenLabel?: boolean;
  dark?: boolean;
  startAdornment?: React.ReactNode;
  endAdornment?: React.ReactNode;
  infoText?: string;
  type?: string;
  disableDecimal?: boolean;
  decimalScale?: number;
  textAlign?: 'center' | 'left' | 'right';
  stopPropagation?: boolean;
};

const TextFieldContent: React.FC<TextFieldContentProps> = ({
  label,
  value,
  error,
  helperText,
  startAdornment,
  endAdornment,
  infoText,
  hiddenLabel,
  textAlign = 'left',
  stopPropagation = false,
  type,
  minRows = 5,
  maxRows = 8,
  dark = false,
  fullWidth = false,
  disabled,
  decimalScale = 3,
  disableDecimal = false,
  onFocus,
  onBlur,
  onChange,
  onKeyDown,
  ...props
}) => {
  const backgroundClass = dark ? 'Mui-backgroundDark' : 'Mui-backgroundLight';

  if (type === 'number')
    return (
      <NumberField
        {...props}
        dark={dark}
        value={value}
        endAdornment={endAdornment}
        onChange={onChange}
        fullWidth={fullWidth}
        label={hiddenLabel ? '' : label}
        decimalScale={decimalScale}
        disableDecimal={disableDecimal}
        disabled={disabled}
        helperText={helperText}
      />
    );

  return (
    <MuiTextField
      {...props}
      label={hiddenLabel ? '' : label}
      value={value ?? ''}
      id={slugify(label)}
      className={backgroundClass}
      fullWidth={fullWidth}
      minRows={minRows}
      maxRows={maxRows}
      helperText={helperText}
      error={error}
      disabled={disabled}
      onKeyDown={(e) => {
        stopPropagation && e.stopPropagation();
        onKeyDown && onKeyDown(e);
      }}
      onFocus={(event) => {
        event.target.select();
        onFocus && onFocus(event);
      }}
      onBlur={onBlur}
      onChange={onChange}
      sx={{
        ...props.sx,
        input: {
          textAlign: textAlign,
        },
      }}
      slotProps={{
        ...props.slotProps,
        inputLabel: {
          className: backgroundClass,
        },
        formHelperText: {
          className: backgroundClass,
        },
        input: (inputProps) => ({
          ref: inputProps.inputRef,
          className: backgroundClass, // Apply to MuiInputBase-root
          startAdornment: startAdornment && <InputAdornment position="start">{startAdornment}</InputAdornment>,
          endAdornment: (
            <>
              {endAdornment && <InputAdornment position="end">{endAdornment}</InputAdornment>}
              {infoText && (
                <InputAdornment position="end">
                  <Tooltip title={infoText}>
                    <Info />
                  </Tooltip>
                </InputAdornment>
              )}
            </>
          ),
        }),
      }}
    />
  );
};

export default React.memo(TextFieldContent);
